import {
    Box,
    Button,
    Group,
    SegmentedControl,
    Stack,
    Text,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore } from '@/features/workspace/hooks';

// 분리된 탭 컴포넌트들 임포트
import ReceivedFeedbackTab from '../components/ReceivedFeedbackTab';
import SentFeedbackTab from '../components/SentFeedbackTab';
import ReceivedRequestsTab from '../components/ReceivedRequestsTab';
import SentRequestsTab from '../components/SentRequestsTab';
import { FeedbackModal } from '../components/FeedbackModal';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';
import {
    useReceivedFeedbackRequests,
    useReceivedFeedbacks,
    useSentFeedbackRequests,
    useSentFeedbacks,
    useMarkFeedbackAsRead,
    useFeedbackCommentsSubscription,
} from '../services/useFeedbackQueries';
import { FeedbackSummary } from '../components/FeedbackSummary';
import { BookmarkedFeedbackList } from '../components/BookmarkedFeedbackList';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    TAB_RECEIVED_FEEDBACK,
    TAB_RECEIVED_REQUEST,
    TAB_SENT_FEEDBACK,
    TAB_SENT_REQUEST,
} from '@/common/constants/constants';
import { usePendingScrollStore } from '@/common/stores/pendingScrollStore';

const FeedbackPage = () => {
    const paddingX = 36;

    const [searchParams, setSearchParams] = useSearchParams();
    // URL에서 tab 파라미터 가져오기
    const tabNameParam = searchParams.get('tab');
    const initialTabName = tabNameParam || TAB_RECEIVED_FEEDBACK; // 파라미터가 없으면 기본값 사용
    const [activeTab, setActiveTab] = useState<string | null>(initialTabName);
    const { target, setScrollTarget } = usePendingScrollStore();

    const { t } = useTranslation();
    const { user } = useAuthStore();
    const userId = user?.id;
    const { currentWorkspace } = useWorkspaceStore();
    const workspace_id = currentWorkspace?.id;
    /* const workspaceWithDetail = useWorkspaceDetails(workspace_id); */

    /*  const teamName = useMemo(() => {
        if (!currentWorkspace || !workspaceWithDetail.data) return '';

        const teams = workspaceWithDetail.data.teams
            ?.filter(team =>
                team.team_member.map(tm => tm.user.id).includes(userId)
            )
            .map(team => team.name)
            .join(', ');

        return teams;
    }, [currentWorkspace, userId, workspaceWithDetail.data]); */

    const { openModal } = useFeedbackModalStore();

    const { data: receivedFeedbacks, isLoading: isLoadingReceived } =
        useReceivedFeedbacks(userId, workspace_id);
    const { data: sentFeedbacks, isLoading: isLoadingSent } = useSentFeedbacks(
        userId,
        workspace_id
    );
    const { data: receivedRequests, isLoading: isLoadingReceivedRequests } =
        useReceivedFeedbackRequests(userId, workspace_id);
    const { data: sentRequests, isLoading: isLoadingSentRequests } =
        useSentFeedbackRequests(userId, workspace_id);

    // 피드백 댓글 구독 - 받은/보낸 피드백 아이디 메모이제이션 후 구독 훅에 전달
    const receivedFeedbackIds = useMemo(
        () => receivedFeedbacks?.map(feedback => feedback.id) || [],
        [receivedFeedbacks]
    );

    const sentFeedbackIds = useMemo(
        () => sentFeedbacks?.map(feedback => feedback.id) || [],
        [sentFeedbacks]
    );

    useFeedbackCommentsSubscription(
        receivedFeedbackIds,
        sentFeedbackIds,
        workspace_id
    );

    const viewedFeedbackIdsRef = useRef<string[]>([]);
    const markFeedbackAsRead = useMarkFeedbackAsRead();

    // URL 파라미터 변경 감지 및 탭 전환
    useEffect(() => {
        const tabNameParam = searchParams.get('tab');
        if (tabNameParam && tabNameParam !== activeTab) {
            setActiveTab(tabNameParam);
        }
    }, [searchParams, activeTab]);

    // 탭 전환 후 스크롤 처리
    useEffect(() => {
        // 약간의 지연을 주어 탭 컴포넌트가 마운트되고 아이템이 렌더링될 시간을 확보
        const timeoutId = setTimeout(() => {
            if (!target) return;

            // 현재 활성 탭과 타겟 타입 일치 확인 (탭에 따라 다른 ID 형식 사용)
            const elementId = `${target.type}-${target.id}`;

            const targetElement = document.getElementById(elementId);
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth' });
                // 스크롤 후 타겟 초기화
                setScrollTarget(null);
            }
        }, 300); // 300ms 지연 - 컴포넌트 마운트와 렌더링을 위한 시간

        return () => clearTimeout(timeoutId);
    }, [activeTab, target, setScrollTarget]);

    // 탭 변경 시 현재 읽지 않은 피드백을 읽음 상태로 변경
    const handleTabChange = (newTab: string | null) => {
        // 이전 탭이 'received'였을 때만 처리 (받은 피드백에만 NEW 라벨이 있음)
        if (
            activeTab === TAB_RECEIVED_FEEDBACK &&
            viewedFeedbackIdsRef.current.length > 0
        ) {
            viewedFeedbackIdsRef.current.forEach(id => {
                markFeedbackAsRead.mutate(id);
            });
            viewedFeedbackIdsRef.current = [];
        }

        // 탭 상태 업데이트 - url 파라미터 업데이트 및 감지로 대체
        // setActiveTab(newTab);

        // URL 파라미터 업데이트
        if (newTab) {
            setSearchParams({ tab: newTab }, { replace: true });
        } else {
            // 기본 탭으로 설정 시 파라미터 제거
            searchParams.delete('tab');
            setSearchParams(searchParams, { replace: true });
        }
    };

    // 컴포넌트 언마운트(페이지 이동) 시 읽지 않은 피드백들을 읽음 상태로 변경
    useEffect(() => {
        return () => {
            if (viewedFeedbackIdsRef.current.length > 0) {
                const idsToUpdate = [...viewedFeedbackIdsRef.current];
                idsToUpdate.forEach(id => {
                    markFeedbackAsRead.mutate(id);
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 의존성 배열을 비워 컴포넌트 마운트/언마운트 시에만 실행

    // 받은 피드백을 화면에 표시할 때 viewedFeedbackIdsRef에 추가
    useEffect(() => {
        if (activeTab === TAB_RECEIVED_FEEDBACK && receivedFeedbacks) {
            const unreadFeedbackIds = receivedFeedbacks
                .filter(feedback => !feedback.is_read)
                .map(feedback => feedback.id);

            if (unreadFeedbackIds.length > 0) {
                viewedFeedbackIdsRef.current = [
                    ...new Set([
                        ...viewedFeedbackIdsRef.current,
                        ...unreadFeedbackIds,
                    ]),
                ];
            }
        }
    }, [activeTab, receivedFeedbacks]);

    const tabsWithCount = useMemo(
        () => [
            {
                value: TAB_RECEIVED_FEEDBACK,
                label: `${t('feedback:tabs.received')} ${!isLoadingReceived && receivedFeedbacks?.length > 0 ? ` (${receivedFeedbacks.length})` : ''}`,
            },
            {
                value: TAB_SENT_FEEDBACK,
                label: `${t('feedback:tabs.sent')} ${!isLoadingSent && sentFeedbacks?.length > 0 ? ` (${sentFeedbacks.length})` : ''}`,
            },
            {
                value: TAB_RECEIVED_REQUEST,
                label: `${t('feedback:tabs.receivedRequest')} ${!isLoadingReceivedRequests && receivedRequests?.length > 0 ? ` (${receivedRequests.length})` : ''}`,
            },
            {
                value: TAB_SENT_REQUEST,
                label: `${t('feedback:tabs.sentRequest')} ${!isLoadingSentRequests && sentRequests?.length > 0 ? ` (${sentRequests.length})` : ''}`,
            },
        ],
        [
            receivedFeedbacks,
            isLoadingReceived,
            sentFeedbacks,
            isLoadingSent,
            receivedRequests,
            isLoadingReceivedRequests,
            sentRequests,
            isLoadingSentRequests,
            t,
        ]
    );

    return (
        <>
            <Group
                pt={26}
                pl={paddingX}
                pr="lg"
                w="100%"
                h="100%"
                gap={0}
                style={{ overflow: 'hidden' }}
            >
                <Stack flex={5} h="100%">
                    <Text size="20px" fw={600}>
                        {t('feedback:title')}
                    </Text>
                    <Box flex={1} style={{ overflow: 'hidden' }}>
                        <Stack gap={0} h="100%">
                            {/* segmented control, action buttons */}
                            <Group
                                justify="space-between"
                                mb="md"
                                pr={paddingX}
                            >
                                <div style={{ width: 'fit-content' }}>
                                    <SegmentedControl
                                        size="xs"
                                        data={tabsWithCount}
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        withItemsBorders={false}
                                    />
                                </div>
                                <Group gap="sm">
                                    <Button
                                        size="sm"
                                        radius="md"
                                        leftSection={<IconSend size={16} />}
                                        onClick={() => {
                                            openModal('send');
                                        }}
                                    >
                                        {t('feedback:action.sendFeedback')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        radius="md"
                                        variant="outline"
                                        onClick={() => {
                                            openModal('request');
                                        }}
                                    >
                                        {t('feedback:action.requestFeedback')}
                                    </Button>
                                </Group>
                            </Group>

                            {activeTab === TAB_RECEIVED_FEEDBACK && (
                                <ReceivedFeedbackTab
                                    receivedFeedbacks={receivedFeedbacks}
                                    isLoadingReceived={isLoadingReceived}
                                />
                            )}

                            {activeTab === TAB_SENT_FEEDBACK && (
                                <SentFeedbackTab
                                    sentFeedbacks={sentFeedbacks}
                                    isLoadingSent={isLoadingSent}
                                />
                            )}

                            {activeTab === TAB_RECEIVED_REQUEST && (
                                <ReceivedRequestsTab
                                    receivedRequests={receivedRequests}
                                    isLoadingReceivedRequests={
                                        isLoadingReceivedRequests
                                    }
                                    notiParams={{
                                        workspaceName: currentWorkspace?.name,
                                        workspaceImageUrl:
                                            currentWorkspace?.profile_image_url,
                                    }}
                                />
                            )}

                            {activeTab === TAB_SENT_REQUEST && (
                                <SentRequestsTab
                                    sentRequests={sentRequests}
                                    isLoadingSentRequests={
                                        isLoadingSentRequests
                                    }
                                    notiParams={{
                                        workspaceName: currentWorkspace?.name,
                                        workspaceImageUrl:
                                            currentWorkspace?.profile_image_url,
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                </Stack>

                <Box
                    flex={2}
                    h="100%"
                    pl="lg"
                    pt="lg"
                    bg="#FAFAFA"
                    style={{ borderRadius: 24 }}
                >
                    <Stack
                        gap="lg"
                        pr="sm"
                        mr="sm"
                        h="100%"
                        style={{
                            overflowY: 'auto',
                        }}
                        className="custom-scrollbar"
                    >
                        {/* ai 피드백 요약 */}
                        {/* <FeedbackSummary /> */}

                        {/* 북마크한 피드백 목록 */}
                        <BookmarkedFeedbackList
                            feedbackList={[
                                ...(receivedFeedbacks || []),
                                ...(sentFeedbacks || []),
                            ]
                                .filter(feedback => feedback.is_bookmarked)
                                .sort(
                                    (a, b) =>
                                        new Date(
                                            b.bookmark_update_date
                                        ).getTime() -
                                        new Date(
                                            a.bookmark_update_date
                                        ).getTime()
                                )}
                        />
                    </Stack>
                </Box>
            </Group>

            <FeedbackModal />
        </>
    );
};

export default FeedbackPage;
